/**
 * What the women's conference page looks like before its data arrives: the
 * painted hero exactly as it will stand (watercolor, flowers, the drawn
 * title), paper below, and the keynote card's shape breathing where it will
 * land. Nothing in it changes when the page fills, so loading reads as the
 * page arriving rather than one page swapping for another. Eager, and tiny:
 * the stock event skeleton is what showed here before.
 */
import { art } from './lib/art';

export default function WhatMattersSkeleton() {
  return (
    <div
      className="xp-wm bleed"
      aria-busy
      style={
        {
          '--art-paper': `url('${art('/art/paper.jpg')}')`,
          '--art-key': `url('${art('/art/what-matters.png')}')`,
        } as React.CSSProperties
      }
    >
      <section className="bg-wm-sage-900 relative overflow-hidden">
        <img
          src={art('/art/watercolor.png')}
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div aria-hidden className="bg-wm-sage-950/40 absolute inset-0" />
        <div className="relative grid grid-cols-[1fr_auto_1fr] pt-[max(4rem,calc(var(--ed-headroom,0px)+2rem))]">
          <div aria-hidden className="pointer-events-none relative lg:overflow-hidden">
            <img
              src={art('/art/flowers-left.png')}
              alt=""
              className="absolute bottom-0 left-0 h-auto w-[24vw] max-w-[13rem] lg:h-full lg:w-auto lg:max-w-none"
            />
          </div>
          <div className="relative z-10 flex min-h-[17rem] flex-col items-center justify-center px-6 pb-20 text-center sm:min-h-[19rem] md:min-h-[21rem] lg:min-h-[23rem]">
            <img
              src={art('/art/what-matters-wordmark.png')}
              alt="What Matters"
              className="h-auto w-full max-w-[19rem] sm:max-w-[26rem] lg:max-w-[28rem]"
            />
            {/* Where the dates and the countdown will be: their height, held. */}
            <div aria-hidden className="mt-5 h-[7.5rem]" />
          </div>
          <div aria-hidden className="pointer-events-none relative lg:overflow-hidden">
            <img
              src={art('/art/flowers-right.png')}
              alt=""
              className="absolute right-0 bottom-0 h-auto w-[19vw] max-w-[10rem] lg:h-full lg:w-auto lg:max-w-none"
            />
          </div>
        </div>
      </section>
      {/* In a block, as the page has its <main>: .xp-wm is a flex column, and
          the shell's auto margins would otherwise shrink it to its content. */}
      <div className="w-full">
        <div className="wm-shell relative z-[61] -mt-10 sm:-mt-12">
          <div className="wm-card grid w-full gap-6 p-6 sm:p-8 md:grid-cols-[auto_1fr] md:items-center">
            <div className="bg-wm-cream mx-auto aspect-[4/3] w-full max-w-sm animate-pulse rounded-2xl md:w-96" />
            <div className="flex flex-col gap-3">
              <div className="bg-wm-cream h-10 w-2/3 animate-pulse rounded-full" />
              <div className="bg-wm-cream h-3 w-1/3 animate-pulse rounded-full" />
              <div className="bg-wm-cream mt-3 h-3 w-full animate-pulse rounded-full" />
              <div className="bg-wm-cream h-3 w-5/6 animate-pulse rounded-full" />
            </div>
          </div>
        </div>
      </div>
      <div className="h-[40vh]" />
    </div>
  );
}
